import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import supplierRequestsService from '../../services/supplierRequestsService';
import './SupplierRequestForm.css';

const SupplierRequestForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    supplier_name: '',
    website_url: '',
    reservation_number: '',
    client_first_name: '',
    client_last_name: '',
    travel_start_date: '',
    travel_end_date: '',
    additional_notes: ''
  });

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.supplier_name.trim()) {
      newErrors.supplier_name = 'Supplier name is required';
    }
    
    if (!formData.reservation_number.trim()) {
      newErrors.reservation_number = 'Reservation number is required';
    }
    
    if (!formData.client_first_name.trim()) {
      newErrors.client_first_name = 'Client first name is required';
    }
    
    if (!formData.client_last_name.trim()) {
      newErrors.client_last_name = 'Client last name is required';
    }

    // Validate website URL format if provided
    if (formData.website_url.trim() && !isValidUrl(formData.website_url)) {
      newErrors.website_url = 'Please enter a valid website URL';
    }

    // Validate travel dates if both are provided
    if (formData.travel_start_date && formData.travel_end_date) {
      if (new Date(formData.travel_start_date) > new Date(formData.travel_end_date)) {
        newErrors.travel_end_date = 'End date must be after start date';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await supplierRequestsService.submit(formData);
      setSubmitted(true);
      
      // Reset form
      setFormData({
        supplier_name: '',
        website_url: '',
        reservation_number: '',
        client_first_name: '',
        client_last_name: '',
        travel_start_date: '',
        travel_end_date: '',
        additional_notes: ''
      });
      
    } catch (error) {
      console.error('Error submitting supplier request:', error);
      setErrors({ submit: error.response?.data?.error || 'Failed to submit supplier request' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (submitted) {
    return (
      <div className="supplier-request-form">
        <div className="success-message">
          <div className="success-icon">✓</div>
          <h2>Request Submitted Successfully!</h2>
          <p>
            Thank you for submitting your supplier request. Our admin team will review the 
            supplier information and get back to you as soon as possible.
          </p>
          <p>
            You will receive an email notification once your request has been reviewed.
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => setSubmitted(false)}
          >
            Submit Another Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="supplier-request-form">
      <div className="form-header">
        <h1>Request New Supplier</h1>
        <p>
          Can't find the supplier you need in our approved list? Submit a request below 
          and our admin team will review it for addition to the system.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="supplier-form">
        {errors.submit && (
          <div className="error-banner">
            {errors.submit}
          </div>
        )}

        {/* Supplier Information Section */}
        <div className="form-section">
          <h3>Supplier Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Supplier Name *</label>
              <input
                type="text"
                value={formData.supplier_name}
                onChange={(e) => handleInputChange('supplier_name', e.target.value)}
                placeholder="Enter the supplier/company name"
                className={errors.supplier_name ? 'error' : ''}
              />
              {errors.supplier_name && <span className="error-text">{errors.supplier_name}</span>}
            </div>
            <div className="form-group">
              <label>Website URL (Optional)</label>
              <input
                type="url"
                value={formData.website_url}
                onChange={(e) => handleInputChange('website_url', e.target.value)}
                placeholder="https://www.supplier-website.com"
                className={errors.website_url ? 'error' : ''}
              />
              {errors.website_url && <span className="error-text">{errors.website_url}</span>}
              <span className="field-hint">Where did you book this reservation?</span>
            </div>
          </div>
        </div>

        {/* Booking Details Section */}
        <div className="form-section">
          <h3>Booking Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Reservation Number *</label>
              <input
                type="text"
                value={formData.reservation_number}
                onChange={(e) => handleInputChange('reservation_number', e.target.value)}
                placeholder="Enter reservation/booking number"
                className={errors.reservation_number ? 'error' : ''}
              />
              {errors.reservation_number && <span className="error-text">{errors.reservation_number}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Client First Name *</label>
              <input
                type="text"
                value={formData.client_first_name}
                onChange={(e) => handleInputChange('client_first_name', e.target.value)}
                placeholder="Enter client's first name"
                className={errors.client_first_name ? 'error' : ''}
              />
              {errors.client_first_name && <span className="error-text">{errors.client_first_name}</span>}
            </div>
            <div className="form-group">
              <label>Client Last Name *</label>
              <input
                type="text"
                value={formData.client_last_name}
                onChange={(e) => handleInputChange('client_last_name', e.target.value)}
                placeholder="Enter client's last name"
                className={errors.client_last_name ? 'error' : ''}
              />
              {errors.client_last_name && <span className="error-text">{errors.client_last_name}</span>}
            </div>
          </div>
        </div>

        {/* Travel Dates Section */}
        <div className="form-section">
          <h3>Travel Dates (Optional)</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Travel Start Date</label>
              <input
                type="date"
                value={formData.travel_start_date}
                onChange={(e) => handleInputChange('travel_start_date', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Travel End Date</label>
              <input
                type="date"
                value={formData.travel_end_date}
                onChange={(e) => handleInputChange('travel_end_date', e.target.value)}
                className={errors.travel_end_date ? 'error' : ''}
              />
              {errors.travel_end_date && <span className="error-text">{errors.travel_end_date}</span>}
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div className="form-section">
          <h3>Additional Information</h3>
          <div className="form-group">
            <label>Additional Notes (Optional)</label>
            <textarea
              value={formData.additional_notes}
              onChange={(e) => handleInputChange('additional_notes', e.target.value)}
              placeholder="Any additional information about the supplier or booking that might be helpful for our review..."
              rows="4"
            />
            <span className="field-hint">
              Include any relevant details about commission rates, booking process, or special requirements
            </span>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Submitting Request...' : 'Submit Supplier Request'}
          </button>
        </div>
      </form>

      <div className="info-section">
        <h4>What happens next?</h4>
        <ul>
          <li>Our admin team will review your supplier request</li>
          <li>We'll verify the supplier's legitimacy and commission structure</li>
          <li>You'll receive an email notification with the decision</li>
          <li>If approved, the supplier will be added to your commission form</li>
        </ul>
      </div>
    </div>
  );
};

export default SupplierRequestForm;
